import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "medication-calendar-table" }
const _hoisted_2 = ["innerHTML"]

import {
    computed,
} from 'vue';

import {
    groupBy,
    Dictionary,
    keyBy,
    partition,
} from 'lodash';

import moment from 'moment';

import {
    Table as ATable,
    Empty as AEmpty,
} from 'ant-design-vue';

import {
    TableColumnOptions,
    useRequest,
} from '../../utils';

import {
    IIntakeForms,
    IMedication,
    IPatientMedicationCalendarItem,
    IMedicationCourse,
} from '../../../life-impulse-tools/types';

import {
    getMedicationMonitoringSets,
} from '../../api';

interface CalendarDataMap {
    medicationMap: {
        [medicationName: string]: true;
    };
    [year: number]: {
        [month: number]: {
            [medicationName: string]: string;
        };
    };
}

interface IPatientMedicationCalendarCombinedItem extends IPatientMedicationCalendarItem {
    firstDoseDate: string;
    lastDoseDate: string;
    doseCount: number;
    intakeForms: Array<string | null>;
    portions: number[];
    someChecked: boolean;
}

interface CombinedDosesMap {
    [key: string]: IPatientMedicationCalendarCombinedItem;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MedicationCalendarTable',
  props: {
    intakeForms: { default: () => [] },
    calendarData: { default: () => [] },
    medicationMap: null,
    medicationCoursesMap: null
  },
  setup(__props: any) {

const props = __props as { intakeForms: IIntakeForms[], calendarData: IPatientMedicationCalendarItem[], medicationMap: Dictionary<IMedication>, medicationCoursesMap: Dictionary<IMedicationCourse> }

/* eslint-disable no-param-reassign */



const [{
    data: medicationSets,
}, doMedicationSetsRequest] = useRequest({
    fetcher: getMedicationMonitoringSets,
    defaultErrorMsg: 'Не удалось загрузить наборы МикроРНК.',
});
doMedicationSetsRequest();

const medicationSetsMap = computed(() => groupBy((medicationSets.value || []).flatMap(
    ({ name, options }) => options.map((option) => ({
        ...option,
        monitoringSetName: name,
    })),
), 'medicationId'));

const intakeFormsMap = computed(() => keyBy(props.intakeForms, 'id'));

const MONTH_NAMES = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const getCombinedDoses = (
    dosesToCombine: IPatientMedicationCalendarItem[],
) => dosesToCombine.reduce((acc: CombinedDosesMap, dose) => {
    if (!acc[dose.courseId]) {
        acc[dose.courseId] = {
            ...dose,
            firstDoseDate: dose.doseDate,
            lastDoseDate: dose.doseDate,
            doseCount: 0,
            intakeForms: [],
            portions: [],
            someChecked: false,
        };
    }
    acc[dose.courseId].doseCount += 1;
    // NOTE: skipped dose
    if (dose.checked === 0) {
        return acc;
    }

    const { times: doseDescriptions } = props.medicationCoursesMap[dose.courseId];

    acc[dose.courseId].lastDoseDate = dose.doseDate;
    if (!acc[dose.courseId].intakeForms.length) {
        acc[dose.courseId].intakeForms = (doseDescriptions || []).map(
            ({ intakeFormId }) => (intakeFormId
                ? intakeFormsMap.value[intakeFormId]?.name
                : null
            ),
        );
        acc[dose.courseId].portions = (doseDescriptions || []).map(
            ({ medicationPortion }) => medicationPortion,
        );
    }
    if (dose.checked) {
        acc[dose.courseId].someChecked = true;
    }
    return acc;
}, {});

const getMedicationColumns = (dose: IPatientMedicationCalendarItem) => {
    // NOTE: sets contain only medication of the latest version
    const currentMedicationId = props.medicationMap[dose.medicationId]?.currentId;
    // NOTE:
    // 1. group by medication set names,
    // medication code or medication name
    // 2. medication can be present in several sets;
    // show medication doses in each set
    return medicationSetsMap.value[currentMedicationId]?.map(
        ({ monitoringSetName }) => monitoringSetName,
    ) || [dose.medicationCode || dose.medicationName];
};

const tablesData = computed(() => {
    const [dosesToCombine, dosesToDivide] = partition(
        props.calendarData,
        ({ medicationId }) => props.medicationMap[medicationId]?.combineInMonitoring,
    );

    const combinedDoses = getCombinedDoses(dosesToCombine);

    let grouppedData = dosesToDivide.reduce((map: CalendarDataMap, dose) => {
        if (dose.checked === 0) {
            return map;
        }
        const names = getMedicationColumns(dose);
        const date = new Date(dose.doseDate);
        const year = date.getFullYear();
        const month = date.getMonth();
        map[year] = map[year] || {};
        map[year][month] = map[year][month] || {};

        names.forEach((name) => {
            map.medicationMap[name] = true;
            map[year][month][name] = map[year][month][name] || '';
        });

        let text = map[year][month][names[0]];
        if (text) {
            text += '<br><br>';
        }
        text += `${moment(dose.doseDate, 'YYYY-MM-DD').format('DD.MM.YYYY')} <br>`;
        if (dose.medicationSubtypeName) {
            text += `${dose.medicationSubtypeName} <br>`;
        }
        if (dose.medicationPortion !== 1) {
            text += `Дозировка: ${dose.medicationPortion} <br>`;
        }
        if (dose.intakeFormId) {
            // TODO: units
            text += `Форма приёма: ${intakeFormsMap.value[dose.intakeFormId]?.name || '?'} <br>`;
        }
        if (!dose.checked) {
            text += 'Не принимал';
        }
        names.forEach((name) => {
            map[year][month][name] = text;
        });
        return map;
    }, { medicationMap: {} });

    grouppedData = Object.keys(combinedDoses).map(
        (id) => combinedDoses[id],
    ).reduce((map: CalendarDataMap, dose) => {
        if (!dose.intakeForms.length) {
            return map;
        }
        const names = getMedicationColumns(dose);
        const date = new Date(dose.doseDate);
        const year = date.getFullYear();
        const month = date.getMonth();

        map[year] = map[year] || {};
        map[year][month] = map[year][month] || {};

        names.forEach((name) => {
            map.medicationMap[name] = true;
            map[year][month][name] = map[year][month][name] || '';
        });

        let text = map[year][month][names[0]];
        if (text) {
            text += '<br><br>';
        }

        text += `${moment(dose.firstDoseDate, 'YYYY-MM-DD').format('DD.MM.YYYY')}`;
        if (dose.firstDoseDate !== dose.lastDoseDate) {
            text += ` - ${moment(dose.lastDoseDate, 'YYYY-MM-DD').format('DD.MM.YYYY')}`;
        }
        text += '<br>';
        if (dose.medicationSubtypeName) {
            text += `${dose.medicationSubtypeName}<br>`;
        }
        text += `${dose.doseCount} приёмов<br>`;
        if (dose.portions.some((portion) => portion !== 1)) {
            text += `Дозировка: ${
                dose.portions.map(
                    (portion, index) => `${index + 1}: ${portion}`,
                ).join(', ')
            } <br>`;
        }
        text += `Форма приёма: ${
            dose.intakeForms.map(
                (intakeForm, index) => (intakeForm ? `${index + 1}: ${intakeForm}` : ''),
            ).filter((s) => s).join(', ') || '-'
        } <br>`;

        if (!dose.someChecked) {
            text += 'Не принимал <br>';
        }
        names.forEach((name) => {
            map[year][month][name] = text;
        });
        return map;
    }, grouppedData);

    const result = Object.keys(grouppedData).map(
        (yearStr) => parseInt(yearStr, 10),
    ).filter((year) => year).map((year) => {
        const yearMap = grouppedData[year];
        const columns: TableColumnOptions[] = [{
            title: `${year}`,
            dataIndex: 'month',
            width: '10%',
        }];
        const medicationNames = Object.keys(grouppedData.medicationMap).sort();
        columns.push(
            ...medicationNames.map((name) => ({
                title: name,
                dataIndex: name,
                slots: { customRender: 'text' },
                width: `${90 / medicationNames.length}%`,
            })),
        );
        const dataSource = Object.keys(yearMap).map((monthStr) => {
            const month = parseInt(monthStr, 10);
            return {
                month: MONTH_NAMES[month],
                ...yearMap[month],
            };
        });
        return {
            year,
            columns,
            dataSource,
        };
    });
    return result;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tablesData), (table) => {
      return (_openBlock(), _createBlock(_unref(ATable), {
        key: table.year,
        columns: table.columns,
        "data-source": table.dataSource,
        pagination: false,
        size: "small",
        "row-key": "month"
      }, {
        text: _withCtx(({ text }) => [
          _createElementVNode("span", { innerHTML: text }, null, 8, _hoisted_2)
        ]),
        _: 2
      }, 1032, ["columns", "data-source"]))
    }), 128)),
    (!_unref(tablesData).length)
      ? (_openBlock(), _createBlock(_unref(AEmpty), {
          key: 0,
          image: _unref(AEmpty).PRESENTED_IMAGE_SIMPLE
        }, {
          description: _withCtx(() => [
            _createTextVNode(" Нет данных. ")
          ]),
          _: 1
        }, 8, ["image"]))
      : _createCommentVNode("", true)
  ]))
}
}

})